/** @module csi/datasheet/_CanvasMixin */
define([
	"dojo/_base/declare",
	"dojo/promise/all",
	"dojo/store/Memory",
	"csi/_base/underscore",
	"csi/api",
	"csi/comm",
	"csi/def/View",
	"csi/html",
	"csi/locale",
	"csi/printUtil",
	"csi/reflection",
	"csi/state",
	"csi/state/rootStore",
	"csi/xml",
	"./Note",
	"./PageWidget",
	"./Preferences",
], function(declare, all, Memory, _, api, comm, View, html, locale, printUtil, reflection, state, {sessionStore}, xml, Note, PageWidget, Preferences) {

	return declare(null, {
		// NOTE: we are mixing preferences here by using Html view preferences for the gallery view
		prefClass: Preferences,

		/** @property {string} canvasCommentsOrientation - indicates canvas position relative to comments ("", "top" or "left"); if empty, then orientation is determined from print orientation */
		canvasCommentsOrientation: "",

		/** @property {boolean} canvasWithProperties - indicates if properties view will be repeated on each canvas page */
		canvasWithProperties: false,

		/** @property {number} canvasWithPropertiesHeight - height of properties view when combined with canvas; it will be subtracted from allowed canvas height */
		canvasWithPropertiesHeight: 110,

		/**
		 * Return true if the current user is able to edit all notes, which is the case for users having admin access to the canvas container
		 * @return {boolean}
		 */
		canEditAllNotes: function() {
			// getViewNode should be the DataSheetPage container (i.e. Theme, DataSheetRevision, etc.)
			return xml.nodeHasRight(this.getViewNode(), xml.RIGHT_ADMIN);
		},

		/**
		 * Return true if canvas should be combined with properties view
		 * @return {boolean}
		 */
		getCanvasCombineWithProperties: function() {
			var printView = this.getToolbarContainerView(true) || this;
			return printView.canvasWithProperties;
		},

		/**
		 * Return reserved height of properties view when combined with canvas
		 * @return {boolean}
		 */
		getCanvasCombineWithPropertiesHeight: function() {
			var printView = this.getToolbarContainerView(true) || this;
			return printView.canvasWithPropertiesHeight;
		},

		/**
		 * Return orientation for canvas; if set to default, then automatically determine best orientation from print orientation
		 * @return {boolean}
		 */
		getCanvasCommentsOrientation: function() {
			var printView = this.getToolbarContainerView(true) || this;
			return printView.canvasCommentsOrientation || ((printView.getPrintOrientation() == api.LANDSCAPE) ? html.LEFT : html.TOP);
		},

		/**
		 * Return store containing CanvasNote objects from given content
		 * @param {string[]} urls
		 * @return {Memory}
		 */
		getCanvasNotesStore: function(urls) {
			const cache = this.cacheView;
			const user = sessionStore.currentUser;
			const bReadOnly = !this.canEditAllNotes();

			cache.cacheNode(user);

			var data = _.map(cache.getNodes(urls), function(node) {
				return {
					id: _.uniqueId(),
					author: cache.getNode(node.Author).$Name,
					authorOnEdit: user.$Name,
					readOnly: bReadOnly,
					text: node.Comment,
					time: node.Date,
					url: node.$URL
				};
			});

			return new Memory({data: data});
		},

		/**
		 * Return layout of page including width, height, canvas/notes orientation and paper orientation
		 * @return {Object}
		 */
		getCanvasPageLayout: function() {
			var printView = this.getToolbarContainerView(true) || this;
			var paper = this.getPrintPaper();
			var orientation = this.getPrintOrientation();
			var layout = printUtil.getPageSizeForSettings(paper, orientation, false, printView.get("printHeader"));

			layout.portrait = (orientation == api.PORTRAIT);
			layout.vertical = this.hasVerticalOrientation();

			if (state.get("print")) {
				// when printing, adjust aspect ratio so that it matches that used when editing; 
				// just make sure not to exceed bounds of print page, so decrease rather than increase
				var printHeight = state.get("printHeight");
				var printWidth = state.get("printWidth");
				var printRatio = printWidth / printHeight;
				var layoutRatio = layout.w / layout.h;

				if (printRatio > layoutRatio) {
					layout.h = printHeight;
					layout.w = printHeight * layoutRatio;
				} else if (printRatio < layoutRatio) {
					layout.h = printWidth / layoutRatio;
					layout.w = printWidth;
				}
			}

			if (printView.canvasWithProperties) {
				layout.h -= printView.canvasWithPropertiesHeight + 1;	// add 1 for border between views
			}

			// subtract 1 for bottom border
			layout.h -= 1;

			// subtract 1 pixel for splitter between canvas and notes
			if (layout.vertical) {
				layout.h -= 1;
			} else {
				layout.w -= 1;
			}

			// the snapshot image overlaps border in PDF, so decrease size when editing by 2px in each dimension to allow for 1px padding around snapshot image when printing
			if (!state.get("print")) {
				layout.h -= 2;
				layout.w -= 2;
			}

			return layout;
		},

		/**
		 * Return true if canvas pane is positioned above comments pane; for canvas views in a composite view, access the settings on the parent view
		 * @return {boolean}
		 */
		hasVerticalOrientation: function() {
			return this.getCanvasCommentsOrientation() == html.TOP;
		},

		/**
		 * Convert CanvasSplit attribute to SplitPct based on canvas width if necessary
		 * @param {Node[]} - list of DataSheetPage nodes
		 */
		migrateCanvasSplits: function(pages) {
			var width = this.getCanvasPageLayout().w;

			_.each(pages, function(page) {
				if ((page.CanvasSplit != null) && (page.SplitPct === -1)) {
					if (page.CanvasSplit <= 1) {
						page.SplitPct = 0;
					} else {
						var x = page.CanvasSplit / width;
						page.SplitPct = Math.min(Number(x.toFixed(3)), 1);
					}
				}
			});
		},

		/**
		 * Ensure that canvas content fits based on titleblock height
		 */
		postRenderCanvasPages: function() {
			var titleBlockView = this.getChildView("TitleBlock");
			var titleBlockNode = titleBlockView && titleBlockView.domNode;

			this.traverseViews(function(view) {
				if (view.defView.m_id == "CanvasPage") {
					view.resizeWithTitleblock(titleBlockNode);
				}
			}, this);
		},

		/**
		 * Render view for each DataSheetPage
		 * @param {Context} ctx
		 * @param {Node[]} pages
		 * @param {DOMNode} rootNode
		 * @param {boolean} bTitleblock
		 * @return {Promise}
		 */
		renderCanvasPages: function(ctx, pages, rootNode, bTitleblock) {
			var css = html.CSS_PAGE + " " + html.CSS_PAGE_BREAK;
			var revision = this.getViewNode();
			var layout = this.getCanvasPageLayout();
			var dfds = [];

			var defView = _.extend(new View(), {
				m_id: "CanvasPage",
				m_typeView: "Html",
				classController: PageWidget
			});

			ctx.forEach(pages, function(ctx) {
				var page = ctx.getNode();
				var image = ctx.cache.getNode(page.Snapshot);
				var snapshot = image ? comm.getFileHref(image[reflection.ATTR_VIEWABLE]) : null;
				var store = this.getCanvasNotesStore(page.Comments);

				if (!page.ThumbnailGenerated) {
					throw locale.resource("ErrorThumbnailMissing");
				}

				dfds.push(ctx.view._insertAndCreateView(defView, revision, rootNode, {
					bTitleblock: bTitleblock,
					cacheView: ctx.cache,
					"class": css,
					notesParams: {
						store: store,
						widgetClass: Note,
						widgetProperties: Note.widgetProperties
					},
					pageHeight: layout.h,
					snapshot: snapshot,
					split: page.SplitPct,
					vertical: layout.vertical
				}));
			}, this);

			return all(dfds);
		}
	});
});